import React, { useState } from 'react';
import { Zap, Eye, EyeOff, ArrowLeft, Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function AuthPage({ mode: initialMode, onBack }) {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, register, adminLogin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Admin shortcut
    if (email === 'admin' && password === 'admin') {
      adminLogin();
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.blob1} />
      <div style={s.blob2} />

      <div style={s.card}>
        <button style={s.backBtn} onClick={onBack}>
          <ArrowLeft size={16} /> Back
        </button>

        <div style={s.logoRow}>
          <div style={s.logoIcon}><Zap size={20} color="#fff" fill="#fff" /></div>
          <span style={s.logoText}>VidFlow</span>
        </div>

        <h2 style={s.title}>
          {mode === 'login' ? 'Welcome back!' : 'Create your account'}
        </h2>
        <p style={s.subtitle}>
          {mode === 'login' ? 'Continue managing your content' : 'Start your free account today'}
        </p>

        {error && <div style={s.errorBox}>{error}</div>}

        <form style={s.form} onSubmit={handleSubmit}>
          {mode === 'register' && (
            <div style={s.field}>
              <label style={s.label}>Full Name</label>
              <input
                style={s.input}
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </div>
          )}
          <div style={s.field}>
            <label style={s.label}>Email</label>
            <input
              style={s.input}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Password</label>
            <div style={s.passWrapper}>
              <input
                style={{ ...s.input, paddingRight: 44 }}
                type={showPass ? 'text' : 'password'}
                placeholder={mode === 'register' ? 'At least 6 characters' : '••••••••'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
              <button type="button" style={s.eyeBtn} onClick={() => setShowPass(!showPass)}>
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button style={s.submitBtn} type="submit" disabled={loading}>
            {loading
              ? <><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Please wait...</>
              : mode === 'login' ? 'Log In' : 'Create Account'}
          </button>
        </form>

        <p style={s.switchText}>
          {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
          {' '}
          <button style={s.switchBtn} onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}>
            {mode === 'login' ? 'Sign Up' : 'Log In'}
          </button>
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input:focus { outline: none; border-color: var(--accent) !important; }
        input::placeholder { color: var(--text-muted); }
      `}</style>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: 'var(--bg-primary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', overflow: 'hidden',
  },
  blob1: {
    position: 'absolute', top: -200, left: -200,
    width: 500, height: 500, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(108,99,255,0.15), transparent)',
    filter: 'blur(60px)',
  },
  blob2: {
    position: 'absolute', bottom: -200, right: -100,
    width: 500, height: 500, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,77,139,0.12), transparent)',
    filter: 'blur(60px)',
  },
  card: {
    width: '100%', maxWidth: 420,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 20, padding: '36px',
    position: 'relative', zIndex: 1,
    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
  },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'none', border: 'none',
    color: 'var(--text-muted)', fontSize: 13,
    cursor: 'pointer', marginBottom: 24, padding: 0,
  },
  logoRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 },
  logoIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 20px var(--accent-glow)',
  },
  logoText: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' },
  title: { fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 },
  subtitle: { fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 },
  errorBox: {
    background: 'rgba(255,77,139,0.1)', border: '1px solid rgba(255,77,139,0.3)',
    borderRadius: 10, padding: '10px 14px',
    fontSize: 13, color: 'var(--pink)', marginBottom: 16,
  },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' },
  input: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '11px 14px',
    color: 'var(--text-primary)', fontSize: 14, width: '100%',
    transition: 'border 0.15s',
  },
  passWrapper: { position: 'relative' },
  eyeBtn: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
  },
  submitBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '13px',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4,
    boxShadow: 'var(--shadow-accent)', transition: 'opacity 0.2s',
  },
  switchText: { textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-secondary)' },
  switchBtn: {
    background: 'none', border: 'none',
    color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', fontSize: 13,
  },
};
